import { useEffect } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useInView } from 'react-intersection-observer'
import { getGetProductsByBrandIdV2QueryKey, getProductsByBrandIdV2 } from '@/apis/rest/generated/hooks'
import { ProductSortByParamParameter } from '@/apis/rest/generated/types'
import { PAGE_LIMIT } from '@/constants/pages.const'
import { useCustomRouter, usePetType } from '@/containers/hooks'
import { useBrandCategory } from '@/containers/hooks/brand/useBrandCategory'
import { useBrandNameQuery } from '@/graphql/generated/hooks'
import { getSortDirectionByOrder, parseSortByOptionSelected } from '@/utils/getSortDirectionByOrder'
import { decodeId } from '@/utils/utilApi'

type BrandDetailQueryType = {
  rootCategoryId: string
  subCategoryId: string
  orderBy: ProductSortByParamParameter
  brandId: string
}

/**
 * ProductsDefault Fragment 필드만 요청하는 products 쿼리를 사용하는 hook
 * FetchMore를 이용한 무한스크롤
 */

const useBrandProductsListV2ContainerHook = () => {
  const { query } = useCustomRouter()
  const { rootCategoryId, subCategoryId, brandId } = query as BrandDetailQueryType
  const orderBy = (query?.orderBy as ProductSortByParamParameter) || 'SALES_SCORE'
  const { ref, inView } = useInView()
  const { categoryData, selectedSubCategoryName, selectedCategoryName, isCategoryLoading } = useBrandCategory()
  const { getPetType } = usePetType()
  const petType = getPetType()

  const { data: brandInfo } = useBrandNameQuery({ variables: { id: brandId } })

  const categoryId = subCategoryId || rootCategoryId ? Number(decodeId(subCategoryId || rootCategoryId).id) : undefined

  const {
    data: brandProducts,
    fetchNextPage,
    hasNextPage,
    isLoading,
  } = useInfiniteQuery(
    getGetProductsByBrandIdV2QueryKey(Number(decodeId(brandId as string).id), {
      sortBy: parseSortByOptionSelected(orderBy) || 'SALES_SCORE',
      sortDirection: getSortDirectionByOrder(orderBy),
      categoryId,
      petType,
    }),
    ({ pageParam = PAGE_LIMIT.DEFAULT_PAGE }) =>
      getProductsByBrandIdV2(Number(decodeId(brandId as string).id), {
        sortBy: parseSortByOptionSelected(orderBy) || 'SALES_SCORE',
        sortDirection: getSortDirectionByOrder(orderBy),
        categoryId,
        page: pageParam,
        size: PAGE_LIMIT.LIST,
        includeSoldOut: true,
        sortSoldOutToLast: true,
        petType,
      }),
    {
      getNextPageParam: (lastPage, allPages) => {
        return lastPage.data?.length === PAGE_LIMIT.LIST ? allPages.length + PAGE_LIMIT.DEFAULT_PAGE : undefined
      },
      enabled: Boolean(petType && brandId),
    }
  )

  useEffect(() => {
    if (inView && hasNextPage && Boolean(petType && brandId)) {
      fetchNextPage()
    }
  }, [inView, fetchNextPage, hasNextPage])

  return {
    isLoading,
    brandInfo,
    isCategoryLoading,
    categoryData,
    brandProducts,
    orderBy,
    ref,
    selectedCategoryName,
    selectedSubCategoryName,
  }
}

export default useBrandProductsListV2ContainerHook
